import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import { ArtifactTypeService } from '../../services/api/artifactTypeService';
import { ArtifactType, CreateArtifactType, UpdateArtifactType } from '../../types/artifactTypes';
import { RootState } from '../index';

interface ArtifactTypeState {
  items: ArtifactType[];
  loading: boolean;
  error: string | null;
}

const initialState: ArtifactTypeState = {
  items: [],
  loading: false,
  error: null,
};

// Helper function to sort artifact types by name
const sortArtifactTypesByName = (types: ArtifactType[]): ArtifactType[] => {
  return [...types].sort((a, b) => 
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );
};

export const fetchArtifactTypes = createAsyncThunk('artifactTypes/fetchAll', async () => {
  return await ArtifactTypeService.getAll();
});

export const createArtifactType = createAsyncThunk(
  'artifactTypes/create',
  async (data: CreateArtifactType) => {
    return await ArtifactTypeService.create(data);
  }
);

export const updateArtifactType = createAsyncThunk(
  'artifactTypes/update',
  async ({ id, data }: { id: string; data: UpdateArtifactType }) => {
    return await ArtifactTypeService.update(id, data);
  }
);

export const deleteArtifactType = createAsyncThunk('artifactTypes/delete', async (id: string) => {
  await ArtifactTypeService.delete(id);
  return id;
});

const artifactTypeSlice = createSlice({
  name: 'artifactTypes',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchArtifactTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArtifactTypes.fulfilled, (state, action) => {
        state.items = sortArtifactTypesByName(action.payload);
        state.loading = false;
      })
      .addCase(fetchArtifactTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch artifact types';
      })
      .addCase(createArtifactType.fulfilled, (state, action) => {
        state.items = sortArtifactTypesByName([...state.items, action.payload]);
        // Clear service cache when new type is created
        ArtifactTypeService.clearCache();
      })
      .addCase(updateArtifactType.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          const updated = [...state.items];
          updated[index] = action.payload;
          state.items = sortArtifactTypesByName(updated);
        }
        // Clear service cache when type is updated
        ArtifactTypeService.clearCache();
      })
      .addCase(deleteArtifactType.fulfilled, (state, action) => {
        state.items = sortArtifactTypesByName(
          state.items.filter((item) => item.id !== action.payload)
        );
        // Clear service cache when type is deleted
        ArtifactTypeService.clearCache();
      });
  },
});

// Selectors
export const selectArtifactTypes = (state: RootState) => state.artifactTypes.items;
export const selectArtifactTypesLoading = (state: RootState) => state.artifactTypes.loading;
export const selectArtifactTypesError = (state: RootState) => state.artifactTypes.error;

// Memoized selector for sorted artifact types (items are already sorted in state, but this ensures consistency)
export const selectSortedArtifactTypes = createSelector(
  [selectArtifactTypes],
  (items) => sortArtifactTypesByName(items)
);

export default artifactTypeSlice.reducer;
